import { LoadingOutlined } from '@ant-design/icons';
import { DefaultOptionType } from '@bugly/common';
import { Select, SelectProps } from 'antd';
import classNames from 'classnames';
import { isNil, isNumber } from 'lodash-es';
import React, { useEffect, useState } from 'react';
import { useStores } from '@/presentation/store';
import { FieldKey, FieldScope, getLocalField } from '@/proto/field';
import './index.module.scss';

export interface TdsFieldSelectorProps extends SelectProps {
  fieldKey?: string;
  fieldScope?: any;
  optionTransform?: (option: DefaultOptionType[]) => DefaultOptionType[];
  // 是否初始加载下拉框数据,用于处理value和label不一致，页面刷新时，没有下拉数据，导致显示错误
  isDefaultQueryOptions?: boolean;
  // 不获取本地配置数据, 处理field name冲突问题
  forceRemote?: boolean;
  // 是否禁用缓存，每次下拉都重新请求远程数据
  disableCache?: boolean;
}

export const TdsFieldSelector = (props: TdsFieldSelectorProps) => {
  const {
    className,
    fieldKey,
    fieldScope,
    options,
    dropdownClassName,
    optionTransform,
    isDefaultQueryOptions,
    forceRemote = false,
    disableCache = false,
    ...restProps
  } = props;

  const { loadField } = useStores().FieldStore;
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [innerOptions, setInnerOptions] = useState<DefaultOptionType[]>(options || []);
  const [, setDropdownVisible] = useState(false);

  const defaultFilterOption = (inputValue: string, option: any) => {
    const valueList = inputValue?.split?.(/;|,/);
    if (valueList?.length > 1) {
      return valueList?.includes?.(option.label);
    }
    return option?.label?.toLowerCase?.()?.indexOf?.(inputValue?.toLowerCase?.() || '') >= 0;
  };

  /**
   * 展开时加载
   * @param open
   */
  const onDropdownVisibleChange = async (open: boolean) => {
    setDropdownVisible(open);
    if (open) {
      await queryOptions();
    }
  };

  const queryOptions = async () => {
    const needRemote = (fieldKey && fieldScope && !options);
    if (needRemote && (!loaded || disableCache)) {
      let field = getLocalField(fieldKey as FieldKey);
      if (field && !forceRemote) {
        setInnerOptions(optionTransform ? optionTransform(field) : field);
      } else {
        setLoading(true);
        // 如果禁用缓存，强制从远程加载
        if (disableCache) {
          field = await loadField(fieldKey as FieldKey, fieldScope as FieldScope, [], false, true);
        } else {
          field = await loadField(fieldKey as FieldKey, fieldScope as FieldScope, [], false);
        }
        setInnerOptions(optionTransform ? optionTransform(field) : field);
        setLoading(false);
      }
      if (!disableCache) {
        setLoaded(true);
      }
    }
  };

  const initOptions = () => {
    if (isDefaultQueryOptions) {
      queryOptions();
      return;
    }
    if (options) {
      setInnerOptions(options);
      return;
    }

    const field = getLocalField(fieldKey as FieldKey);
    setInnerOptions(field || []);
  };

  useEffect(() => {
    initOptions();
  }, [options]);

  return (
    <Select
      placeholder={'请选择'}
      className={classNames('tds-form-select', className)}
      allowClear
      showArrow
      options={innerOptions.filter((item) => {
        if (isNumber(item)) return true;
        return !isNil(item.value);
      }).map(item => ({
        ...item,
        ['data-tid']: `${name}-${item.label}`,
      }))}
      dropdownClassName={classNames('tds-form-select-dropdown', dropdownClassName)}
      showSearch
      autoClearSearchValue
      filterOption={defaultFilterOption}
      maxTagCount={'responsive'}
      onDropdownVisibleChange={onDropdownVisibleChange}
      dropdownRender={originNode => (loading
        ? <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}><LoadingOutlined /></div>
        : originNode)}
      {...restProps}
    />
  );
};
